import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  FormControl,
  Select,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import CancelIcon from "@mui/icons-material/Cancel";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../../Layout/GRISidebar";
import EastIcon from "@mui/icons-material/East";
import { put, get } from "../../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../../../../styles/gri/general.module.css";
import SectionHeading from "../GRI308/SectionHeading";

import ReviewBox from "../../../../Components/Common/ReviewBox";
import { currentYear } from "../../../../utils/generateYearRanges";

const CustomTextField = ({
  placeholder,
  value,
  onChange,
  name,
  sx = {},
  disabled,
  ...props
}) => {
  return (
    <TextField
      fullWidth
      placeholder={placeholder}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e, name)}
      sx={{
        marginTop: "-0.5rem",
        "& .MuiInputBase-root": {
          height: 40,
          // padding: "0 8px",
        },
        "& .MuiInputBase-input": {
          fontSize: 14,
        },
        ...sx,
      }}
      {...props}
    />
  );
};

const OperationalSitesForm = ({hide}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [sites, setSites] = useState([
    {
      operationalSite: "",
      locationDetails: { country: "", region: "", locality: "" },
      landOwernship: "",
      highBiodiversity: { proximity: "", outsideZones: "" },
      operationalSiteType: "",
      siteArea: "",
      characteristics: { ecosystem: "", attributes: "" },
      desingationFrameworks: "",
    },
  ]);

  const [expanded, setExpanded] = useState([true]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState(null);
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));

  useEffect(() => {
    const handleStorage = () => {
      setGriReview(JSON.parse(localStorage.getItem("griReview")));
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    setGriReview(JSON.parse(localStorage.getItem("griReview")));
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/gri/get?Id=${id}&type=GRI300`);
        if (response?.data?.data?.year < currentYear - 1) setDisabled(true);
        const data =
          response.data.data.GRI300?.operationalSitesBiodiversity || [];
        // Ensure at least one empty entry exists
        const mappedSites =
          data.length > 0
            ? data.map((site) => ({
                operationalSite: site.operationalSite || "",
                locationDetails: {
                  country: site.locationDetails?.country || "",
                  region: site.locationDetails?.region || "",
                  locality: site.locationDetails?.locality || "",
                },
                landOwernship: site.landOwernship || "",
                highBiodiversity: {
                  proximity: site.highBiodiversity?.proximity || "",
                  outsideZones: site.highBiodiversity?.outsideZones || "",
                },
                operationalSiteType: site.operationalSiteType || "",
                siteArea: site.siteArea || "",
                characteristics: {
                  ecosystem: site.characteristics?.ecosytem || "",
                  attributes: site.characteristics?.attributes || "",
                },
                desingationFrameworks: site.desingationFrameworks || "",
              }))
            : [
                {
                  operationalSite: "",
                  locationDetails: { country: "", region: "", locality: "" },
                  landOwernship: "",
                  highBiodiversity: { proximity: "", outsideZones: "" },
                  operationalSiteType: "",
                  siteArea: "",
                  characteristics: { ecosystem: "", attributes: "" },
                  desingationFrameworks: "",
                },
              ]; // Ensuring at least one blank site

        setSites(mappedSites);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  //If want to fill all the form then replace || with &&
  const isSiteComplete = (site) => {
    return (
      site.operationalSite.trim() !== "" ||
      site.locationDetails.country.trim() !== "" ||
      site.locationDetails.region.trim() !== "" ||
      site.locationDetails.locality.trim() !== "" ||
      site.landOwernship.trim() !== "" ||
      site.highBiodiversity.proximity.trim() !== "" ||
      site.highBiodiversity.outsideZones.trim() !== "" ||
      site.operationalSiteType.trim() !== "" ||
      site.desingationFrameworks.trim() !== "" ||
      site.characteristics.ecosystem.trim() !== "" ||
      site.characteristics.attributes.trim() !== "" ||
      site.siteArea.trim() !== ""
    );
  };

  // Handle input changes for a specific site
  const handleChange = (e, field, index) => {
    const newSites = [...sites];
    newSites[index][field] = e.target.value;
    setSites(newSites);
  };

  const handleNestedChange = (e, name, index) => {
    const [parent, child] = name.split(".");
    const newSites = [...sites];
    newSites[index][parent][child] = e.target.value;
    setSites(newSites);
  };

  const handleNestedDropDownChange = (e, index, parent, child) => {
    const newSites = [...sites];
    newSites[index][parent][child] = e.target.value;
    setSites(newSites);
  };

  const addNewSite = () => {
    if (!isSiteComplete(sites[sites.length - 1])) {
      setSnackOpen({
        open: true,
        message: "Please complete the previous entry before adding a new one.",
        severity: "warning",
      });
      return;
    }
    setSites([
      ...sites,
      {
        operationalSite: "",
        locationDetails: { country: "", region: "", locality: "" },
        landOwernship: "",
        highBiodiversity: { proximity: "", outsideZones: "" },
        operationalSiteType: "",
        siteArea: "",
        characteristics: { ecosystem: "", attributes: "" },
        desingationFrameworks: "",
      },
    ]);

    // Automatically close all sections and expand only the newly added one
    const newExpanded = sites.map(() => false); // Collapse all current sections
    setExpanded([...newExpanded, true]); // Expand only the new section
  };

  const toggleExpand = (index) => {
    const newExpanded = [...expanded];
    newExpanded[index] = !newExpanded[index];
    setExpanded(newExpanded);
  };

  const handleOpenConfirmDialog = (index) => {
    // setSiteToDelete(index);
    // setOpenConfirmDialog(true);
    if (sites.length > 1) {
      setSiteToDelete(index);
      setOpenConfirmDialog(true);
    }
  };

  // Close the dialog
  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setSiteToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (siteToDelete !== null && sites.length > 1) {
      const newSites = sites.filter((_, i) => i !== siteToDelete);
      setSites(newSites);
      const newExpanded = expanded.filter((_, i) => i !== siteToDelete);
      setExpanded(newExpanded);
    }
    handleCloseConfirmDialog();
  };

  const handleSubmit = async () => {
    if (!disabled) {
      // Utility function to check if an entry has all fields empty
      const isEntryEmpty = (site) => {
        return (
          !site.operationalSite &&
          !site.locationDetails.country &&
          !site.locationDetails.region &&
          !site.locationDetails.locality &&
          !site.landOwernship &&
          !site.highBiodiversity.proximity &&
          !site.highBiodiversity.outsideZones &&
          !site.operationalSiteType &&
          !site.siteArea &&
          !site.characteristics.ecosystem &&
          !site.characteristics.attributes &&
          !site.desingationFrameworks
        );
      };

      const filteredSites = sites.filter((site) => !isEntryEmpty(site));

      const payload = {
        type: "GRI300",
        submitted304_1: true,
        operationalSitesBiodiversity: filteredSites.map((site) => ({
          operationalSite: site.operationalSite,
          locationDetails: {
            country: site.locationDetails.country,
            region: site.locationDetails.region,
            locality: site.locationDetails.locality,
          },
          landOwernship: site.landOwernship,
          highBiodiversity: {
            proximity: site.highBiodiversity.proximity,
            outsideZones: site.highBiodiversity.outsideZones,
          },
          operationalSiteType: site.operationalSiteType,
          siteArea: site.siteArea,
          characteristics: {
            ecosytem: site.characteristics.ecosystem,
            attributes: site.characteristics.attributes,
          },
          desingationFrameworks: site.desingationFrameworks,
        })),
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/gri300/304-2/${id}`);
      } catch (error) {
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/gri300/304-2/${id}`);
    }
  };


  const renderContent = ({hide}) => (
    <>
      <Dialog open={openConfirmDialog} onClose={handleCloseConfirmDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you really want to delete this entry?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {!hide && <p className={styles.section_heading} style={{marginBottom : hide ? "-1rem" : "0", }}>304 : Biodiversity</p>}

      <p className={styles.main_heading} style={{marginBottom : hide ? "-1rem" : "0", }}>
        304-1 : Operational sites owned, leased, managed in, or adjacent to,
        protected areas and areas of high biodiversity value outside protected
        areas
      </p>
      {sites.map((site, index) => (
        <Box key={index} sx={{ marginBottom: "0.5rem" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <SectionHeading
              text={`Operational Site: ${site.operationalSite || "New Site"}`}
              sx={{ fontSize: "20px" }}
            />
            <Box>
              <IconButton
                sx={{ cursor: "pointer" }}
                onClick={() => toggleExpand(index)}
              >
                {/* {expanded[index] ? <ExpandLess /> : <ExpandMore />} */}
                {expanded[index] ? (
                  <ArrowDropUpIcon sx={{ color: "black" }} />
                ) : (
                  <ArrowDropDownIcon sx={{ color: "black" }} />
                )}
              </IconButton>

              <IconButton
                // onClick={() => deleteSite(index)}
                onClick={() => handleOpenConfirmDialog(index)}
                disabled={sites.length <= 1 || disabled}
              >
                <CancelIcon />
              </IconButton>
            </Box>
          </Box>
          {expanded[index] && (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <SectionHeading
                text="a. Could you please provide details for each operational site that is either owned, leased, managed within, or located adjacent to protected areas, as well as areas of high biodiversity value that are outside protected areas?"
                sx={{ marginTop: "-0.2rem" }}
              />

              {/* I: Operational Site */}

              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ width: "60%", marginLeft: "1rem" }} // Ensure it adjusts well to container width
              >
                {/* Label */}
                <Box sx={{}}>
                  <SectionHeading text="Operational Site:" />
                </Box>

                {/* Input Field */}
                <Box sx={{ width: "60%" }}>
                  <CustomTextField
                    placeholder="Enter Text"
                    value={site.operationalSite}
                    onChange={(e) => handleChange(e, "operationalSite", index)}
                    disabled={disabled}
                  />
                </Box>
              </Box>

              {/* Location Details */}
              <Box sx={{ marginLeft: "1rem" }}>
                <SectionHeading text="i. Location Details:" />
                <Box
                  display="flex"
                  flexDirection="row"
                  gap={3}
                  sx={{ marginTop: "-1rem" }}
                >
                  <Box flex={1} sx={{ marginLeft: "0.7rem" }}>
                    <SectionHeading text="Country" />
                    <CustomTextField
                      placeholder="Enter Text"
                      fullWidth
                      value={site.locationDetails.country}
                      disabled={disabled}
                      onChange={(e) =>
                        handleNestedChange(e, "locationDetails.country", index)
                      }
                      //   onChange={handleNestedChange}
                      //   name="locationDetails.country"
                    />
                  </Box>

                  <Box flex={1}>
                    <SectionHeading text="Region" />
                    <CustomTextField
                      placeholder="Enter Text"
                      value={site.locationDetails.region}
                      onChange={(e) =>
                        handleNestedChange(e, "locationDetails.region", index)
                      }
                      disabled={disabled}
                    />
                  </Box>
                  <Box flex={1}>
                    <SectionHeading text="Locality" />
                    <CustomTextField
                      placeholder="Enter Text"
                      value={site.locationDetails.locality}
                      onChange={(e) =>
                        handleNestedChange(e, "locationDetails.locality", index)
                      }
                      disabled={disabled}
                    />
                  </Box>
                </Box>

                {/* II: Land Ownership */}

                <FormControl fullWidth margin="normal">
                  <SectionHeading text="ii. Land Ownership and Management:" />
                  <Box sx={{ marginLeft: "1rem" }}>
                    <SectionHeading
                      text="Subsurface and Underground Land Status"
                      sx={{
                        fontWeight: 500,
                        marginTop: "-0.3rem",
                        marginBottom: "-0.1rem",
                      }}
                    />
                    <RadioGroup
                      row
                      value={site.landOwernship}
                      onChange={(e) => handleChange(e, "landOwernship", index)}
                      sx={{ gap: "1rem" }}
                      disabled={disabled}
                    >
                      <FormControlLabel
                        value="Owned"
                        control={<Radio disabled={disabled} />}
                        label="Owned"
                      />
                      <FormControlLabel
                        value="Leased"
                        control={<Radio disabled={disabled} />}
                        label="Leased"
                      />
                      <FormControlLabel
                        value="Managed"
                        control={<Radio disabled={disabled} />}
                        label="Managed"
                      />
                    </RadioGroup>
                  </Box>
                </FormControl>

                {/* III: High Biodiversity */}
                <SectionHeading text="iii. Relationship to Protected Areas or High Biodiversity Value Areas:" />
                <Box sx={{ marginLeft: "1rem" }}>
                  <SectionHeading
                    text="Proximity to protected areas (inside, adjacent to, or containing portions of)"
                    sx={{ fontWeight: 500, marginTop: "-0.3rem" }}
                  />
                  <CustomTextField
                    placeholder="Enter Text"
                    value={site.highBiodiversity.proximity}
                    onChange={(e) =>
                      handleNestedChange(e, "highBiodiversity.proximity", index)
                    }
                    disabled={disabled}
                  />
                  <SectionHeading
                    text="Relation to high biodiversity value areas outside protected zones"
                    sx={{ fontWeight: 500 }}
                  />
                  <CustomTextField
                    placeholder="Enter Text"
                    value={site.highBiodiversity.outsideZones}
                    onChange={(e) =>
                      handleNestedChange(
                        e,
                        "highBiodiversity.outsideZones",
                        index
                      )
                    }
                    disabled={disabled}
                  />
                </Box>

                {/* IV: Operational Site Type */}
                <SectionHeading text="iv. Operational Site Type:" />

                <FormControl
                  fullWidth
                  margin="normal"
                  sx={{ marginLeft: "1.3rem" }}
                >
                  <SectionHeading
                    text="Nature of Operation"
                    sx={{
                      fontWeight: 500,
                      marginTop: "-1.2rem",
                      marginBottom: "-0.1rem",
                    }}
                  />
                  {/* <FormLabel>Nature of Operation</FormLabel> */}
                  <RadioGroup
                    row
                    value={site.operationalSiteType}
                    onChange={(e) =>
                      handleChange(e, "operationalSiteType", index)
                    }
                    sx={{ gap: "1rem" }}
                    disabled={disabled}
                  >
                    <FormControlLabel
                      value="Office"
                      control={<Radio disabled={disabled} />}
                      label="Office"
                    />
                    <FormControlLabel
                      value="Manufacturing/Production"
                      control={<Radio disabled={disabled} />}
                      label="Manufacturing/Production"
                    />
                    <FormControlLabel
                      value="Extractive"
                      control={<Radio disabled={disabled} />}
                      label="Extractive"
                    />
                  </RadioGroup>
                </FormControl>

                {/* V: Site Area */}
                <SectionHeading text="v. Site Area:" />
                <Box sx={{ marginLeft: "1rem" }}>
                  <SectionHeading
                    text="Size of operational site in square kilometers"
                    sx={{ fontWeight: 500, marginTop: "-0.5rem" }}
                  />

                  <CustomTextField
                    sx={{ width: "35%" }}
                    placeholder="Enter Number"
                    value={site.siteArea}
                    // onChange={(e) => handleChange(e, "siteArea", index)}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*\.?\d*$/.test(value)) {
                        handleChange(e, "siteArea", index);
                      }
                    }}
                    name="siteArea"
                    disabled={disabled}
                  />
                </Box>

                {/* VI: Biodiversity Characteristics */}
                <SectionHeading text="vi. Biodiversity Characteristics:" />
                <Box
                  display="flex"
                  gap={2}
                  sx={{ width: "70%", marginLeft: "1.3rem" }}
                >
                  <FormControl fullWidth>
                    <SectionHeading
                      text="Ecosystem Type"
                      sx={{
                        fontWeight: 500,
                        marginTop: "-0.5rem",
                      }}
                    />
                    <Select
                      value={sites[index]?.characteristics?.ecosystem || ""}
                      displayEmpty
                      onChange={(e) =>
                        handleNestedDropDownChange(
                          e,
                          index,
                          "characteristics",
                          "ecosystem"
                        )
                      }
                      sx={{
                        height: 40,
                        marginTop: "-0.5rem",
                        "& .MuiSelect-select": {
                          padding: "8px 10px",
                          fontSize: 14,
                        },
                      }}
                      disabled={disabled}
                    >
                      <MenuItem value="" disabled>
                        Select
                      </MenuItem>
                      <MenuItem value="Terrestrial">Terrestrial</MenuItem>
                      <MenuItem value="Freshwater">Freshwater</MenuItem>
                      <MenuItem value="Marine">Marine</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <SectionHeading
                      text="Key Biodiversity Attributes"
                      sx={{
                        fontWeight: 500,
                        marginTop: "-0.5rem",
                      }}
                    />
                    <CustomTextField
                      placeholder="Enter Text"
                      value={site.characteristics.attributes}
                      disabled={disabled}
                      onChange={(e) =>
                        handleNestedChange(
                          e,
                          "characteristics.attributes",
                          index
                        )
                      }
                    />
                  </FormControl>
                </Box>

                {/* VII: Designation Frameworks */}
                <SectionHeading text="vii. Designation under relevant conservation frameworks (IUCN Protected Area Management Categories, Ramsar Convention, national legislation)" />
                <Box sx={{ marginLeft: "1rem" }}>
                  <CustomTextField
                    placeholder="Enter Text"
                    value={site.desingationFrameworks}
                    disabled={disabled}
                    onChange={(e) =>
                      handleChange(e, "desingationFrameworks", index)
                    }
                    name="desingationFrameworks"
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      ))}

      {/* <Box
        sx={{
          display: "flex",
          height: "10rem",
          justifyContent: "space-between",
          marginTop: "2rem",
        }}
      > */}
      {!hide &&
      <Box sx={{ marginLeft: "2rem", marginTop: "2rem" }}>
        <Button
          startIcon={<Add />}
          onClick={addNewSite}
          disabled={
            disabled || 
            (sites.length > 0 && !isSiteComplete(sites[sites.length - 1]))
          }
          sx={{
            // marginTop: "1rem",
            color: "#1C486B",
            width: "16rem",
            borderColor: "#1C486B",
            padding: "4px 0px",
            minHeight: "10px",
            textTransform: "none",
            fontWeight: 600,
          }}
          variant="outlined"
        >
          Add more operational site
        </Button>
      </Box>
}
      {/* submit button */}

      {/* </Box> */}

      
      <Snackbar
        open={snackOpen.open}
        message={snackOpen.message}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSnackOpen({ ...snackOpen, open: false })}
      >
        <Alert
          severity={snackOpen.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackOpen.message}
        </Alert>
      </Snackbar>
    </>
  )
  return (
      <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
        <Box sx={{ ml: "1rem", width: "98%" }}>
          <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
        </Box>
      ) : (
        <Box textAlign="right">
          <Button
            variant="contained"
            sx={{ bgcolor: "#1C486B", textTransform: "none" }}
            onClick={handleSubmit}
          >
            Save & Continue <EastIcon sx={{ ml: 2 }} />
          </Button>
        </Box>
      )}

          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default OperationalSitesForm;
